// src/utils/api.js
export class ApiUtil {
  constructor(config = {}) {
    this.config = config || {};
    this.base = this.config.auditEndpoint || '/api';
    this.apiKey = this.config.apiKey || null;
    this.retryAttempts = Number(this.config.retryAttempts || 2);
    this.timeout = Number(this.config.timeout || 15000);
  }

  _buildHeaders(extra = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...extra
    };
    if (this.apiKey) headers['x-api-key'] = this.apiKey;
    return headers;
  }

  async post(path, body = {}, opts = {}) {
    return this._request('POST', path, body, opts);
  }

  async get(path, opts = {}) {
    return this._request('GET', path, null, opts);
  }

  async _request(method, path, body = null, opts = {}) {
    const url = this._buildUrl(path);
    const headers = this._buildHeaders(opts.headers || {});
    const fetchOpts = {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
      credentials: opts.credentials || 'omit'
    };

    let attempt = 0;
    while (attempt <= this.retryAttempts) {
      try {
        const res = await this._fetchWithTimeout(url, fetchOpts, this.timeout);
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          let err = new Error(`HTTP ${res.status}: ${res.statusText}`);
          err.status = res.status;
          err.body = text;
          throw err;
        }
        // attempt to parse JSON
        const json = await res.json().catch(() => null);
        return json;
      } catch (err) {
        attempt++;
        if (attempt > this.retryAttempts) {
          // return structured error object rather than throwing for SDK resilience
          return {
            error: true,
            message: err.message,
            status: err.status || null,
            body: err.body || null
          };
        }
        // small backoff
        await this._sleep(200 * attempt);
      }
    }
  }

  _buildUrl(path) {
    if (!path) return this.base;
    // path may already be an absolute URL
    if (/^https?:\/\//i.test(path)) return path;
    return `${this.base.replace(/\/+$/,'')}/${path.replace(/^\/+/,'')}`;
  }

  _fetchWithTimeout(url, opts, timeoutMs) {
    const controller = new AbortController();
    const signal = controller.signal;
    opts.signal = signal;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, opts).finally(() => clearTimeout(timer));
  }

  _sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
  }
}
